import { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Lightning } from '@phosphor-icons/react';
import { API } from '@/App';

const POLL_INTERVAL_MS = 8000;
const ROTATE_INTERVAL_MS = 3500;

function formatRelative(iso) {
  try {
    const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  } catch (parseErr) {
    // Invalid ISO date — non-fatal
    console.debug('Relative time parse failed:', parseErr);
    return '';
  }
}

const LiveTicketFeed = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchFeed = async () => {
      try {
        const r = await fetch(`${API}/tickets/recent-public?limit=20`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setItems(d.items || []);
      } catch (feedErr) {
        // Feed polling — non-fatal, retry next interval
        console.debug('Recent tickets feed fetch failed:', feedErr);
      }
    };
    fetchFeed();
    timerRef.current = setInterval(fetchFeed, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const rot = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_INTERVAL_MS);
    return () => clearInterval(rot);
  }, [items.length]);

  if (items.length === 0) {
    return null;
  }

  const current = items[idx % items.length];
  const flag = current.country ? `${current.country}` : '';
  const label = current.playerLabel || 'P.';

  return (
    <div
      data-testid="live-ticket-feed"
      className="mb-6 bg-[#1a1508]/60 border border-[#cba135]/15 rounded-lg px-4 py-2.5 flex items-center gap-3 overflow-hidden"
    >
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400/60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 font-semibold">LIVE</span>
      </div>
      <Lightning size={14} weight="duotone" className="text-[#cba135] shrink-0" />
      <div className="flex-1 min-w-0 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p
            key={`${current.ticketNumber}-${idx}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="text-sm text-[#cba135]/85 truncate"
          >
            <span className="font-semibold text-[#cba135]">{label}</span>
            {flag && <span className="text-[#cba135]/60"> from {flag}</span>}
            <span className="text-[#cba135]/60"> grabbed ticket </span>
            <span className="font-semibold text-[#cba135]">#{current.ticketNumber}</span>
            <span className="text-[#cba135]/40 ml-2 text-xs">· {formatRelative(current.purchasedAt)}</span>
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LiveTicketFeed;
